<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupQuestionsClosingCard, {QuestionsClosingAnswers} from "@/medication-checkup/components/MedicationCheckupQuestionsClosingCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {CounsellingClosingView} from "@/medication-checkup/interfaces/counselling-closing";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useClosingDocument} from "@/medication-checkup/composables/useClosingDocument";
import {useKnowledgeAppraisal} from "@/medication-checkup/composables/useKnowledgeAppraisal";

/**
 * The scene closing the advice interview, right after the plan is
 * presented: the end of the third step.
 *
 * The words, the acts to bill and — once validated — the questions, the
 * synthesis and the appraisal recorded and the form to download are served
 * by the handler.
 * Typing is the scene's own state: the closing action, always available
 * since nothing is required, submits it all. It closes on no line of its own: the follow-up appointment
 * is planned by the scene that follows.
 */

const props = defineProps<SceneProps<CounsellingClosingView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, acted, act: submit} = useScene(props, {card: true});

const form = useClosingDocument(
    computed(() => view.value?.counselling_recap ?? null),
    'entretien-conseil',
    "L'entretien conseil n'a pas pu être téléchargé."
);

/**
 * The form the closing offers, shown from the start and served once
 * validated.
 */
const downloads = computed(() => [{
    key: 'counselling_recap',
    label: "Télécharger l'entretien conseil (PDF)",
    available: !!view.value?.counselling_recap,
    downloading: form.downloading.value
}]);

/**
 * The texts typed so far — local until the closing action submits them.
 */
const answers = ref<QuestionsClosingAnswers>({questions: '', conclusion: ''});

const {appraisal, submitted} = useKnowledgeAppraisal();

/**
 * Type into one field — local only: the closing action submits both.
 */
const typed = (field: keyof QuestionsClosingAnswers, value: string) => {
    answers.value = {...answers.value, [field]: value};
};

/**
 * Take the closing action, submitting the two texts and the appraisal.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    questions: answers.value.questions.trim() || null,
    conclusion: answers.value.conclusion.trim() || null,
    ...submitted()
});
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupQuestionsClosingCard
        v-if="view"
        headline="Votre entretien conseil est terminé !"
        :recorded="view"
        :billing="view.billing"
        :downloads="downloads"
        frozen
        @download="form.download"
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupQuestionsClosingCard
        v-if="cardShown && view"
        headline="Votre entretien conseil est terminé !"
        :recorded="view"
        :billing="view.billing"
        :downloads="downloads"
        :answers="answers"
        :appraisal="appraisal"
        class="animate-message-in"
        @typed="typed"
        @appraised="appraisal = $event"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        @act="act"
      />
    </template>
  </div>
</template>
